import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  RotateCcw, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Calendar
} from 'lucide-react';
import Mixpanel from '../utils/mixpanel';
import logger from '../utils/logger';
import GuideModal from './GuideModal';
import StandardWidthContainer from './StandardWidthContainer';


const API_URL = 'https://backend-rough-bird-115.fly.dev';

const DailySuccessCheckList = () => {
  // ...existing state declarations...

  // Helper: Check if today is Monday
  const isTodayMonday = () => {
    // getDay(): 1 = Monday
    return new Date().getDay() === 1;
  };

  // Helper: Get start of the current week (Monday)
  const getMondayOfCurrentWeek = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - ((day + 6) % 7); // Monday as start
    const monday = new Date(d.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday.toISOString();
  };

  useEffect(() => {
    // Only reset on Monday and if not already reset this week
    const lastReset = localStorage.getItem('lastWeekReset');
    const mondayThisWeek = getMondayOfCurrentWeek();
    if (isTodayMonday() && lastReset !== mondayThisWeek) {
      // Reset all checkboxes to gray (pending) for the new week
      setActions(prevActions => prevActions.map(action => ({
        ...action,
        dailyStatus: action.dailyStatus ? action.dailyStatus.map(status => ({
          ...status,
          status: 'pending'
        })) : []
      })));
      localStorage.setItem('lastWeekReset', mondayThisWeek);
      logger.info('Weekly checklist reset for new week.');
    }
  }, []); // Run only on mount

  const [date, setDate] = useState(new Date());
  const [actions, setActions] = useState([]);
  const [newAction, setNewAction] = useState({
    action: '',
    category: 'Other',
    frequency: 'daily',
    timeOfDay: 'anytime'
  });
  const [editingAction, setEditingAction] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messageText, setMessageText] = useState(null);
  const [messageType, setMessageType] = useState(null); // 'success' or 'error'
  const [selectedDay, setSelectedDay] = useState(new Date().toISOString().split('T')[0]);
  const [expandedActions, setExpandedActions] = useState({});
  const [renderCount, setRenderCount] = useState(0);
  const [buttonStatus, setButtonStatus] = useState({});
  const [currentDate] = useState(new Date()); // Store the current date when component mounts

  // Define days of week and current day - reordered to start on Monday
  const daysOfWeek = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
  // Adjust getDay() to make Monday first day (0) and Sunday last day (6)
  const currentDay = daysOfWeek[(new Date().getDay() + 6) % 7];

  // Predefined categories
  const categories = [
    'Physical Health',
    'Mental Health',
    'Career',
    'Relationships',
    'Personal Development',
    'Wellness',
    'Other'
  ];

  // Frequency options
  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekdays', label: 'Weekdays Only' },
    { value: 'weekends', label: 'Weekends Only' },
    { value: 'custom', label: 'Custom Days' }
  ];

  // Time of day options
  const timeOfDayOptions = [
    { value: 'morning', label: 'Morning' },
    { value: 'afternoon', label: 'Afternoon' },
    { value: 'evening', label: 'Evening' },
    { value: 'anytime', label: 'Anytime' }
  ];

  // Get day checkboxes for mobile view
  const getDayCheckboxes = (action) => {
    return daysOfWeek.map(day => (
      <div key={day} className="flex flex-col items-center">
        <div className="text-xs text-gray-500 mb-1">{day}</div>
        <button
          onClick={() => toggleStatus(action._id, day)}
          className={`h-8 w-8 rounded-full ${
            getVisualStatus(action, day) === 'completed'
              ? 'bg-green-500 hover:bg-green-600'
              : getVisualStatus(action, day) === 'missed'
                ? 'bg-red-500 hover:bg-red-600'
                : 'bg-gray-200 hover:bg-gray-300'
          } ${!isDayEditable(day) ? 'cursor-not-allowed opacity-70' : ''}`}
          title={
            !isDayEditable(day) 
              ? 'You can only update the current day and previous day' 
              : getEffectiveStatus(action, day) === 'completed' 
                ? 'Mark as not completed' 
                : 'Mark as completed'
          }
          disabled={!isDayEditable(day)}
        >
          {getEffectiveStatus(action, day) === 'completed' && (
            <span className="text-white flex items-center justify-center h-full w-full">✓</span>
          )}
          {getVisualStatus(action, day) === 'missed' && (
            <span className="text-white flex items-center justify-center h-full w-full">!</span>
          )}
        </button>
      </div>
    ));
  };

  // Safely handle action data to prevent rendering errors
  const safeAction = (action) => {
    if (!action) return {};
    return {
      ...action,
      action: typeof action.action === 'string' ? action.action : 'Task',
      category: typeof action.category === 'string' ? action.category : 'Other',
      completionRate: typeof action.completionRate === 'number' ? action.completionRate : 0
    };
  };

  // Get status for a specific day
  const getStatusForDay = (action, day) => {
    if (!action || !action.dailyStatus || !Array.isArray(action.dailyStatus)) {
      return 'pending';
    }
    const status = action.dailyStatus.find(s => s && s.day === day);
    return status ? status.status : 'pending';
  };

  // Get effective status based on frequency and day
  const getEffectiveStatus = (action, day) => {
    if (!action) return 'pending';
    
    // Check if this day is applicable based on frequency
    const isApplicable = (() => {
      const dayIndex = daysOfWeek.indexOf(day);
      if (action.frequency === 'daily') return true;
      if (action.frequency === 'weekdays') return dayIndex > 0 && dayIndex < 6;
      if (action.frequency === 'weekends') return dayIndex === 0 || dayIndex === 6;
      return true; // Default to applicable
    })();
    
    if (!isApplicable) return 'not-applicable';
    
    return getStatusForDay(action, day);
  };

  // Toggle status for a specific day
  const toggleStatus = async (actionId, day) => {
    try {
      // Find the action
      const action = actions.find(a => a._id === actionId);
      if (!action) {
        logger.error('Action not found:', actionId);
        return;
      }
      
      // Get current status
      const currentStatus = getStatusForDay(action, day);
      const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
      
      // Update local state immediately for responsive UI
      const updatedActions = actions.map(a => {
        if (a._id === actionId) {
          const updatedDailyStatus = a.dailyStatus ? [...a.dailyStatus] : [];
          const statusIndex = updatedDailyStatus.findIndex(s => s && s.day === day);
          
          if (statusIndex >= 0) {
            updatedDailyStatus[statusIndex] = {
              ...updatedDailyStatus[statusIndex],
              status: newStatus,
              timestamp: new Date()
            };
          } else {
            updatedDailyStatus.push({
              day,
              status: newStatus,
              timestamp: new Date()
            });
          }
          
          return {
            ...a,
            dailyStatus: updatedDailyStatus
          };
        }
        return a;
      });
      
      setActions(updatedActions);
      
      // Update button status
      setButtonStatus({
        ...buttonStatus,
        [`${actionId}-${day}`]: newStatus
      });
      
      // API call to update status
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/daily-actions/${actionId}/day/${day}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Track the event
      Mixpanel.track('Toggle Daily Action Status', {
        actionId,
        day,
        newStatus,
        actionName: action.action
      });
      
      // Check if this is the first habit check
      if (newStatus === 'completed') {
        markFirstHabitCheckCompleted();
      }
      
    } catch (error) {
      logger.error('Error toggling status:', error);
      // Revert the local state change on error
      fetchActions();
    }
  };

  // Fetch actions from the API
  const fetchActions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        // For demo purposes, generate sample data if no token
        const sampleData = generateSampleData();
        setActions(sampleData);
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/daily-actions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Ensure data is safe for rendering
      const enhancedData = response.data.map(action => safeAction(action));
      
      setActions(enhancedData);
      setLoading(false);
      
    } catch (error) {
      logger.error('Error fetching actions:', error);
      setError('Failed to load your daily actions. Please try again later.');
      setLoading(false);
    }
  };

  // Handle adding a new action
  const handleAddAction = async () => {
    try {
      if (!newAction.action.trim()) {
        alert('Please enter an action description');
        return;
      }
      
      const token = localStorage.getItem('token');
      
      // Prepare dailyStatus array with pending status for all days
      const dailyStatus = daysOfWeek.map(day => ({
        day,
        status: 'pending',
        timestamp: new Date()
      }));
      
      const actionData = {
        ...newAction,
        dailyStatus,
        order: actions.length
      };
      
      const response = await axios.post(`${API_URL}/api/daily-actions`, actionData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Add the new action to the state
      const safeNewAction = safeAction(response.data);
      setActions([...actions, safeNewAction]);
      
      // Reset form and close it
      setNewAction({
        action: '',
        category: 'Other',
        frequency: 'daily',
        timeOfDay: 'anytime'
      });
      setShowNewForm(false);
      
      // Track the event
      Mixpanel.track('Add Daily Action', {
        actionName: newAction.action,
        category: newAction.category,
        frequency: newAction.frequency
      });
      
    } catch (error) {
      logger.error('Error adding action:', error);
      alert('Failed to add action. Please try again.');
    }
  };

  // Handle editing an action
  const handleEditAction = (action) => {
    setEditingAction({...action});
    setShowEditModal(true);
  };

  // Handle changes to the edit form
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingAction({
      ...editingAction,
      [name]: value
    });
  };

  // Handle saving edits
  const handleSaveEdit = async () => {
    try {
      if (!editingAction.action.trim()) {
        alert('Please enter an action description');
        return;
      }
      
      const token = localStorage.getItem('token');
      
      // Make API call to update the action
      await axios.put(`${API_URL}/api/daily-actions/${editingAction._id}`, editingAction, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update the action in the local state
      const updatedActions = actions.map(action => 
        action._id === editingAction._id ? safeAction(editingAction) : action
      );
      
      setActions(updatedActions);
      
      // Close the modal
      setEditingAction(null);
      setShowEditModal(false);
      
      // Track the event
      Mixpanel.track('Edit Daily Action', {
        actionId: editingAction._id,
        actionName: editingAction.action,
        category: editingAction.category,
        frequency: editingAction.frequency
      });
      
    } catch (error) {
      logger.error('Error updating action:', error);
      alert('Failed to update action. Please try again.');
    }
  };

  // Handle deleting an action
  const handleDeleteAction = async (actionId) => {
    if (!window.confirm('Are you sure you want to delete this action?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Make API call to delete the action
      await axios.delete(`${API_URL}/api/daily-actions/${actionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove the action from the local state
      const updatedActions = actions.filter(action => action._id !== actionId);
      setActions(updatedActions);
      
      // Track the event
      Mixpanel.track('Delete Daily Action', {
        actionId
      });
      
    } catch (error) {
      logger.error('Error deleting action:', error);
      alert('Failed to delete action. Please try again.');
    }
  };

  // Handle resetting all actions
  const handleResetActions = async () => {
    if (!window.confirm('Are you sure you want to reset all actions to pending?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Make API call to reset all actions
      await axios.post(`${API_URL}/api/daily-actions/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reset all actions in the local state
      const resetActions = actions.map(action => ({
        ...action,
        dailyStatus: action.dailyStatus.map(status => ({
          ...status,
          status: 'pending',
          timestamp: new Date()
        }))
      }));
      
      setActions(resetActions);
      
      // Reset button status
      setButtonStatus({});
      
      // Track the event
      Mixpanel.track('Reset All Daily Actions');
      
    } catch (error) {
      logger.error('Error resetting actions:', error);
      alert('Failed to reset actions. Please try again.');
    }
  };

  // Toggle expanding an action in mobile view
  const toggleActionExpand = (actionId) => {
    setExpandedActions({
      ...expandedActions,
      [actionId]: !expandedActions[actionId]
    });
  };

  // Handle changes to the new action form
  const handleNewActionChange = (e) => {
    const { name, value } = e.target;
    setNewAction({
      ...newAction,
      [name]: value
    });
  };

  // Get day of week from date string
  const getDayOfWeek = (dateString) => {
    const date = new Date(dateString);
    return daysOfWeek[date.getDay()];
  };

  // Check if a day is editable (current day or previous day only)
  // Restrict editing to today and yesterday only
  const isDayEditable = (day) => {
    const today = new Date();
    // Our daysOfWeek: ['MO', 'TU', ...], JS getDay(): 0=Sunday, 1=Monday...
    // So: Monday = 1, but index 0 in daysOfWeek
    const todayIndex = (today.getDay() + 6) % 7; // 0=Monday, 6=Sunday
    const dayIndex = daysOfWeek.indexOf(day);

    // Calculate the date for the given day in this week
    const monday = new Date(today);
    monday.setDate(today.getDate() - todayIndex);
    const dayDate = new Date(monday);
    dayDate.setDate(monday.getDate() + dayIndex);

    // Only allow editing today or yesterday
    const diffDays = Math.floor((today - dayDate) / (1000 * 60 * 60 * 24));
    return diffDays === 0 || diffDays === 1;
  };

  // Weekly reset logic: reset all dailyStatus to 'pending' on Monday morning
  useEffect(() => {
    const today = new Date();
    const todayIndex = (today.getDay() + 6) % 7; // 0=Monday
    const lastReset = localStorage.getItem('lastWeekReset');
    const lastResetDate = lastReset ? new Date(lastReset) : null;
    const todayMidnight = new Date(today);
    todayMidnight.setHours(0,0,0,0);

    // If today is Monday and last reset was before today
    if (todayIndex === 0 && (!lastResetDate || lastResetDate < todayMidnight)) {
      // Reset all actions' dailyStatus to pending
      setActions(prevActions => prevActions.map(action => ({
        ...action,
        dailyStatus: action.dailyStatus.map(ds => ({ ...ds, status: 'pending' }))
      })));
      localStorage.setItem('lastWeekReset', todayMidnight.toISOString());
      // Optionally, send reset to backend here
    }
  }, []);

  // Check if a day should be marked as missed
  const isDayMissed = (day) => {
    const today = new Date();
    const todayIndex = today.getDay();
    const dayIndex = daysOfWeek.indexOf(day);
    
    // If it's a future day, it's not missed
    if ((dayIndex > todayIndex) || 
        (todayIndex === 0 && dayIndex > 0)) {
      return false;
    }
    
    // If it's more than one day in the past, it could be missed
    const twoDaysAgoIndex = todayIndex <= 1 ? todayIndex + 5 : todayIndex - 2;
    return dayIndex <= twoDaysAgoIndex && dayIndex !== todayIndex;
  };

  // Get visual status for a day
  const getVisualStatus = (action, day) => {
    const effectiveStatus = getEffectiveStatus(action, day);
    
    // If it's already completed, show as completed
    if (effectiveStatus === 'completed') return 'completed';
    
    // If it's not applicable for this day based on frequency, show as not applicable
    if (effectiveStatus === 'not-applicable') return 'not-applicable';
    
    // If it's a missed day, show as missed
    if (isDayMissed(day)) return 'missed';
    
    // Otherwise, it's pending
    return 'pending';
  };

  // Mark first habit check completed
  const markFirstHabitCheckCompleted = () => {
    const firstHabitCheck = localStorage.getItem('firstHabitCheck');
    if (!firstHabitCheck) {
      localStorage.setItem('firstHabitCheck', 'completed');
      
      // Track the event
      Mixpanel.track('First Habit Check Completed');
      
      // Show a message
      setMessageText('Great job completing your first habit check!');
      setMessageType('success');
      
      // Clear the message after 5 seconds
      setTimeout(() => {
        setMessageText(null);
        setMessageType(null);
      }, 5000);
    }
  };

  // Generate sample data for demo purposes
  const generateSampleData = () => {
    return [
      {
        _id: 'sample1',
        action: 'Exercise for 30 minutes',
        category: 'Physical Health',
        frequency: 'daily',
        timeOfDay: 'morning',
        dailyStatus: daysOfWeek.map(day => ({
          day,
          status: 'pending',
          timestamp: new Date()
        })),
        order: 0
      },
      {
        _id: 'sample2',
        action: 'Meditate for 10 minutes',
        category: 'Mental Health',
        frequency: 'daily',
        timeOfDay: 'morning',
        dailyStatus: daysOfWeek.map(day => ({
          day,
          status: 'pending',
          timestamp: new Date()
        })),
        order: 1
      },
      {
        _id: 'sample3',
        action: 'Read for 20 minutes',
        category: 'Personal Development',
        frequency: 'daily',
        timeOfDay: 'evening',
        dailyStatus: daysOfWeek.map(day => ({
          day,
          status: 'pending',
          timestamp: new Date()
        })),
        order: 2
      }
    ];
  };

  // Fetch actions on component mount
  useEffect(() => {
    fetchActions();
    
    // Check if we need to reset for the new week
    // (Old shouldResetWeek logic removed; handled elsewhere)

    
    // Track page view
    Mixpanel.track('View Daily Success Checklist');
  }, []);

  // Update render count for debugging
  useEffect(() => {
    setRenderCount(prevCount => prevCount + 1);
  }, [actions, buttonStatus, expandedActions]);

  if (loading) {
    return (
      <div className="alert alert-info mb-4">
        <div className="flex-1">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-6 h-6 mx-2 stroke-current">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>Loading your daily actions...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error mb-4">
        <span>{error}</span>
      </div>
    );
  }

  return (
    <>
      <GuideModal guideKey="Habits" />
      <div className="container px-4 pt-4">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
            <h1 className="text-2xl font-bold mb-4 sm:mb-0">Daily Success Checklist</h1>
            <div className="flex items-center space-x-2">
              <span className="text-gray-600 mr-2">{format(date, 'EEEE, MMMM d, yyyy')}</span>
            </div>
          </div>
          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => setShowNewForm(!showNewForm)}
              className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded flex items-center"
            >
              <Plus className="mr-1" size={18} />
              New Action
            </button>
            <button
              onClick={handleResetActions}
              className="bg-yellow-500 hover:bg-yellow-600 text-white py-2 px-4 rounded flex items-center text-sm sm:text-base"
            >
              <RotateCcw className="mr-1" size={18} />
              Reset All
            </button>
          </div>
        </div>

      {showNewForm && (
        <div className="mb-8 bg-white p-4 sm:p-6 rounded-lg shadow">
          <h2 className="text-lg font-bold mb-4">Add New Action</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <input
                type="text"
                name="action"
                value={newAction.action}
                onChange={handleNewActionChange}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="What do you want to accomplish?"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                name="category"
                value={newAction.category}
                onChange={handleNewActionChange}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a category</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <select
                  name="frequency"
                  value={newAction.frequency}
                  onChange={handleNewActionChange}
                  className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {frequencyOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time of Day</label>
                <select
                  name="timeOfDay"
                  value={newAction.timeOfDay}
                  onChange={handleNewActionChange}
                  className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {timeOfDayOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                onClick={() => setShowNewForm(false)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAction}
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded flex items-center"
              >
                <Plus className="mr-1" size={18} />
                Add Action
              </button>
            </div>
          </div>
        </div>
      )}

      {editingAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Edit Action</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <input
                type="text"
                name="action"
                value={editingAction.action}
                onChange={handleEditChange}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                name="category"
                value={editingAction.category}
                onChange={handleEditChange}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <select
                  name="frequency"
                  value={editingAction.frequency}
                  onChange={handleEditChange}
                  className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {frequencyOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time of Day</label>
                <select
                  name="timeOfDay"
                  value={editingAction.timeOfDay}
                  onChange={handleEditChange}
                  className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {timeOfDayOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setEditingAction(null);
                  setShowEditModal(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile View */}
      <div className="block lg:hidden space-y-4">
        {Array.isArray(actions) && actions.length > 0 ? (
          actions.map((action) => (
            <div key={action._id} className="bg-white rounded-lg shadow overflow-hidden mb-4">
              <div
                className="flex justify-between items-center p-4 cursor-pointer"
                onClick={() => toggleActionExpand(action._id)}
              >
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleStatus(action._id, currentDay);
                    }}
                    className={`h-6 w-6 rounded-full ${getEffectiveStatus(action, currentDay) === 'completed'
                      ? 'bg-green-500'
                      : 'bg-gray-200'}`}
                    data-action-id={action._id}
                    data-day={currentDay}
                  >
                    {getEffectiveStatus(action, currentDay) === 'completed' && (
                      <span className="text-white flex items-center justify-center h-full w-full">✓</span>
                    )}
                  </button>
                  <div>
                    <div className="font-medium text-gray-900">
                      {typeof action.action === 'string' ? action.action : 'Task'}
                      <span className="text-sm text-blue-600">
                        {(() => {
                          try {
                            if (!action || !action.dailyStatus || !Array.isArray(action.dailyStatus)) {
                              return '(0%)';
                            }
                            const completedDays = action.dailyStatus.filter(s => s && s.status === 'completed').length;
                            const totalDays = action.dailyStatus.length;
                            return totalDays > 0 ? `(${Math.round((completedDays / totalDays) * 100)}%)` : '(0%)';
                          } catch (e) {
                            logger.error('Error calculating completion rate:', e);
                            return '(0%)';
                          }
                        })()}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 flex flex-wrap">
                      <span className="mr-2">{action.category}</span>
                      {action.timeOfDay !== 'anytime' && (
                        <span className="mr-2">• {timeOfDayOptions.find(o => o.value === action.timeOfDay)?.label}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {expandedActions[action._id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditAction(action);
                    }}
                    className="text-blue-500 hover:text-blue-700"
                    title="Edit action"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAction(action._id);
                    }}
                    className="text-red-500 hover:text-red-700"
                    title="Delete action"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              {expandedActions[action._id] && (
                <div className="p-4 border-t border-gray-100">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <p className="text-sm text-gray-500">
                        <span className="font-medium">Frequency:</span> {frequencyOptions.find(o => o.value === action.frequency)?.label}
                      </p>
                      <p className="text-sm text-gray-500">
                        <span className="font-medium">Time of Day:</span> {timeOfDayOptions.find(o => o.value === action.timeOfDay)?.label}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-7 gap-1 mt-2">
                    {getDayCheckboxes(action)}
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="bg-white p-6 rounded-lg shadow text-center">
            <p className="text-gray-500">No actions yet. Add your first daily action above!</p>
          </div>
        )}
      </div>
      
      {/* Desktop View */}
      <div className="hidden lg:block">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
              {daysOfWeek.map((day) => (
                <th
                  key={day}
                  className={`px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider ${day === currentDay ? 'bg-blue-50' : ''}`}
                >
                  {day}
                </th>
              ))}
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.isArray(actions) && actions.length > 0 ? (
              actions.map((action) => (
                <tr key={action._id}>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => toggleStatus(action._id, currentDay)}
                        className={`h-6 w-6 rounded-full ${getEffectiveStatus(action, currentDay) === 'completed'
                          ? 'bg-green-500'
                          : 'bg-gray-200'}`}
                        data-action-id={action._id}
                        data-day={currentDay}
                      >
                        {getEffectiveStatus(action, currentDay) === 'completed' && (
                          <span className="text-white flex items-center justify-center h-full w-full">✓</span>
                        )}
                      </button>
                      <div>
                        <div className="font-medium text-gray-900">
                          {typeof action.action === 'string' ? action.action : 'Task'}
                          <span className="text-sm text-blue-600">
                            {(() => {
                              try {
                                if (!action || !action.dailyStatus || !Array.isArray(action.dailyStatus)) {
                                  return '(0%)';
                                }
                                const completedDays = action.dailyStatus.filter(s => s && s.status === 'completed').length;
                                const totalDays = action.dailyStatus.length;
                                return totalDays > 0 ? `(${Math.round((completedDays / totalDays) * 100)}%)` : '(0%)';
                              } catch (e) {
                                logger.error('Error calculating completion rate:', e);
                                return '(0%)';
                              }
                            })()}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 flex flex-wrap">
                          <span className="mr-2">{action.category}</span>
                          {action.timeOfDay !== 'anytime' && (
                            <span className="mr-2">• {timeOfDayOptions.find(o => o.value === action.timeOfDay)?.label}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  {daysOfWeek.map((day) => (
                    <td
                      key={day}
                      className={`px-2 py-4 text-center ${day === currentDay ? 'bg-blue-50' : ''}`}
                    >
                      <button
                        onClick={() => toggleStatus(action._id, day)}
                        className={`h-8 w-8 rounded-full ${getVisualStatus(action, day) === 'completed'
                          ? 'bg-green-500 hover:bg-green-600'
                          : getVisualStatus(action, day) === 'missed'
                            ? 'bg-red-500 hover:bg-red-600'
                            : 'bg-gray-200 hover:bg-gray-300'} ${!isDayEditable(day) ? 'cursor-not-allowed opacity-70' : ''}`}
                        title={!isDayEditable(day)
                          ? 'You can only update the current day and previous day'
                          : getEffectiveStatus(action, day) === 'completed'
                            ? 'Mark as not completed'
                            : 'Mark as completed'}
                        disabled={!isDayEditable(day)}
                      >
                        {getEffectiveStatus(action, day) === 'completed' && (
                          <span className="text-white flex items-center justify-center h-full w-full">✓</span>
                        )}
                        {getVisualStatus(action, day) === 'missed' && (
                          <span className="text-white flex items-center justify-center h-full w-full">!</span>
                        )}
                      </button>
                    </td>
                  ))}
                  <td className="px-4 py-4 text-center">
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => handleEditAction(action)}
                        className="text-blue-500 hover:text-blue-700"
                        title="Edit action"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteAction(action._id)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete action"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                  No actions yet. Add your first daily action above!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>
    </>
  );
};

export default DailySuccessCheckList;
